import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CampaignsAPI from '../../api/campaigns';
import AnalyticsHelper from '../../helper/AnalyticsHelper';
import { CAMPAIGNS_EVENTS } from '../../helper/AnalyticsHelper/events';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isCreating: false,
  },
  pagination: {
    total: 0,
    total_pages: 0,
    current_page: 1,
    per_page: 25,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCampaigns: _state => campaignType => {
    return _state.records.filter(
      record => record.campaign_type === campaignType
    );
  },
  getAllCampaigns: _state => {
    return _state.records;
  },
};

// Monotonic token for `get`. Typing/tab-switching fires overlapping requests;
// without sequencing a slower earlier response can resolve last and overwrite a
// newer list + pagination meta with stale data. Only the latest request commits.
let latestGetRequestId = 0;
let pendingGetCount = 0;

export const actions = {
  get: async function getCampaigns({ commit }, params = {}) {
    const { skipStore = false, ...apiParams } = params;
    let requestId = null;
    if (!skipStore) {
      latestGetRequestId += 1;
      requestId = latestGetRequestId;
      pendingGetCount += 1;
      commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: true });
    }
    try {
      const response = await CampaignsAPI.get(apiParams);
      const hasEnvelope = Boolean(response.data?.data && response.data?.meta);
      const campaigns = hasEnvelope ? response.data.data : response.data || [];
      const meta = hasEnvelope ? response.data.meta : null;
      if (!skipStore && requestId === latestGetRequestId) {
        commit(types.SET_CAMPAIGNS, campaigns);
        if (meta) commit(types.SET_CAMPAIGN_PAGINATION, meta);
      }
      return { campaigns, meta: meta || {}, ok: true };
    } catch (error) {
      return { campaigns: [], meta: {}, ok: false };
    } finally {
      // The flag is released by the last request to settle, not by the latest
      // one: a stale response must still decrement, otherwise a superseded fetch
      // leaves isFetching stuck on until the next successful get.
      if (!skipStore) {
        pendingGetCount -= 1;
        if (pendingGetCount === 0) {
          commit(types.SET_CAMPAIGN_UI_FLAG, { isFetching: false });
        }
      }
    }
  },
  create: async function createCampaign({ commit }, campaignObj) {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: true });
    try {
      const response = await CampaignsAPI.create(campaignObj);
      commit(types.ADD_CAMPAIGN, response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isCreating: false });
    }
  },
  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: true });
    try {
      const response = await CampaignsAPI.update(id, updateObj);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.UPDATE_CAMPAIGN);
      commit(types.EDIT_CAMPAIGN, response.data);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isUpdating: false });
    }
  },
  delete: async ({ commit }, id) => {
    commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: true });
    try {
      await CampaignsAPI.delete(id);
      AnalyticsHelper.track(CAMPAIGNS_EVENTS.DELETE_CAMPAIGN);
      commit(types.DELETE_CAMPAIGN, id);
    } catch (error) {
      throw error;
    } finally {
      commit(types.SET_CAMPAIGN_UI_FLAG, { isDeleting: false });
    }
  },
  previewFilter: async (_, { payload, channel }) => {
    const response = await CampaignsAPI.previewFilter(payload, channel);
    return response.data;
  },
  previewLabels: async (_, { labelIds, channel }) => {
    const response = await CampaignsAPI.previewLabels(labelIds, channel);
    return response.data;
  },
  previewCsv: async (_, { blobSignedId, channel }) => {
    const response = await CampaignsAPI.previewCsv(blobSignedId, channel);
    return response.data;
  },
};

export const mutations = {
  [types.SET_CAMPAIGN_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_CAMPAIGN_PAGINATION](_state, meta) {
    _state.pagination = {
      ..._state.pagination,
      total: meta.total || meta.total_count || 0,
      total_pages: meta.total_pages || 0,
      current_page: meta.current_page || 1,
      per_page: meta.per_page || 25,
      agenda_count: meta.agenda_count ?? _state.pagination.agenda_count ?? 0,
      history_count: meta.history_count ?? _state.pagination.history_count ?? 0,
    };
  },

  [types.ADD_CAMPAIGN]: MutationHelpers.create,
  [types.SET_CAMPAIGNS]: MutationHelpers.set,
  [types.EDIT_CAMPAIGN]: MutationHelpers.update,
  [types.DELETE_CAMPAIGN]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
